import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Label } from '@/components/ui/Label';
import { getAllOfficialTests, normalizeTestId, normalizeBatteryForDerived } from '@/config/batteries';
import { BatterySelectorList } from '@/components/app/BatterySelectorList';
import {
  type TestKey,
  type RoleType,
  getInstrumentId,
  normalizeSelectedTests,
  getExpertBenchmark,
  ROLE_TYPE_IDS,
  ROLE_TYPE_LABELS,
  normalizeRoleType,
} from '@/config/tests';
import { getRecommendedTests } from '@/lib/testRecommendation';
import { INDUSTRIES } from '@/config/industries';
import type { IndustryId } from '@/config/industries';

export function ProcessBatteryPage() {
  const { id } = useParams<{ id: string }>();
  const { appUser, canAccessCompany } = useAuth();
  const [processTitle, setProcessTitle] = useState('');
  const [companyIndustry, setCompanyIndustry] = useState<IndustryId | null>(null);
  const [presetIndustry, setPresetIndustry] = useState<IndustryId | null>(null);
  const [roleType, setRoleType] = useState<RoleType | ''>('');
  const [selected, setSelected] = useState<string[]>([]);
  const [userModifiedBattery, setUserModifiedBattery] = useState(true); // true = no sobrescribir batería cargada
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!id || !appUser) return;
    const load = async () => {
      const processSnap = await getDoc(doc(db, 'processes', id));
      if (!processSnap.exists() || !canAccessCompany(processSnap.data().companyId)) {
        setLoaded(true);
        return;
      }
      const companyId = processSnap.data().companyId;
      setProcessTitle(processSnap.data().title ?? '');
      const data = processSnap.data();
      const rt = normalizeRoleType(data.roleType);
      setRoleType((rt as RoleType) ?? '');
      if (companyId) {
        const companySnap = await getDoc(doc(db, 'companies', companyId));
        if (companySnap.exists()) {
          const ind = (companySnap.data().industry as IndustryId) ?? null;
          setCompanyIndustry(ind);
          setPresetIndustry((prev) => (prev == null ? ind : prev));
        }
      }
      const processTestKeys = (data.testKeys ?? []) as TestKey[];
      const batterySnap = await getDoc(doc(db, 'processBatteries', id));
      const batteryModuleIds = batterySnap.exists()
        ? ((batterySnap.data().moduleIds ?? []) as string[])
        : [];
      const rawLoaded = processTestKeys.length > 0 ? processTestKeys : batteryModuleIds.map(normalizeTestId);
      const loadedBattery = normalizeBatteryForDerived(rawLoaded);
      setSelected(normalizeSelectedTests(loadedBattery));
      setLoaded(true);
    };
    load();
  }, [id, appUser, canAccessCompany]);

  const industryForPreset = presetIndustry ?? companyIndustry;
  const availableTests = getAllOfficialTests();
  const presetApplied = Boolean(
    industryForPreset && roleType && !userModifiedBattery
  );

  const handlePresetIndustryChange = (value: string) => {
    const ind = (value || null) as IndustryId | null;
    setPresetIndustry(ind);
    setUserModifiedBattery(false);
    if (ind && roleType) {
      const recommended = getRecommendedTests(ind, roleType);
      if (recommended.length > 0) setSelected(recommended);
    }
  };

  const handlePresetRoleTypeChange = (value: string) => {
    const rt = (value || '') as RoleType | '';
    setRoleType(rt);
    setUserModifiedBattery(false);
    if (industryForPreset && rt) {
      const recommended = getRecommendedTests(industryForPreset, rt);
      if (recommended.length > 0) setSelected(recommended);
    }
  };

  const toggle = (testId: string) => {
    setUserModifiedBattery(true);
    setSelected((prev) => {
      const next = prev.includes(testId) ? prev.filter((x) => x !== testId) : [...prev, testId];
      return normalizeSelectedTests(next);
    });
  };

  const save = async () => {
    if (!id) return;
    setSaving(true);
    const validIds = normalizeSelectedTests(selected.filter((s) => availableTests.some((t) => t.testId === s)));
    const expertProfile = getExpertBenchmark(roleType as RoleType);
    try {
      await setDoc(
        doc(db, 'processBatteries', id),
        { processId: id, moduleIds: validIds, updatedAt: new Date().toISOString() },
        { merge: true }
      );
      await updateDoc(doc(db, 'processes', id), {
        testKeys: validIds,
        jobProfile: expertProfile ?? null,
        ...(roleType ? { roleType } : {}),
        updatedAt: new Date().toISOString(),
      });
      setSelected(validIds);
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) return <p className="text-muted-foreground">Cargando...</p>;

  return (
    <div className="space-y-6">
      <Button variant="ghost" size="sm" asChild>
        <Link to={`/app/processes/${id}`}>← {processTitle || 'Proceso'}</Link>
      </Button>
      <Card>
        <CardHeader>
          <CardTitle>Batería del proceso</CardTitle>
          <p className="text-sm text-muted-foreground">
            Selecciona las pruebas que formarán parte de este proceso. El candidato las verá con su
            nombre comercial.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-4">
            <h3 className="text-sm font-medium">Configuración del Puesto</h3>
            <p className="text-xs text-muted-foreground">
              Selecciona el área y tipo de puesto para aplicar la preselección inteligente de pruebas recomendadas.
            </p>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="presetIndustry">Área / Industria</Label>
                <select
                  id="presetIndustry"
                  value={presetIndustry ?? companyIndustry ?? ''}
                  onChange={(e) => handlePresetIndustryChange(e.target.value)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <option value="">Selecciona el área</option>
                  {INDUSTRIES.map((ind) => (
                    <option key={ind.id} value={ind.id}>
                      {ind.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="presetRoleType">Tipo de Puesto</Label>
                <select
                  id="presetRoleType"
                  value={roleType}
                  onChange={(e) => handlePresetRoleTypeChange(e.target.value)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <option value="">Selecciona el tipo de puesto</option>
                  {ROLE_TYPE_IDS.map((id) => (
                    <option key={id} value={id}>
                      {ROLE_TYPE_LABELS[id]}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            {presetApplied && (
              <p className="text-xs text-green-600 dark:text-green-500">
                Batería recomendada aplicada según el área y tipo de puesto seleccionados.
              </p>
            )}
            {roleType && getExpertBenchmark(roleType as RoleType) && (
              <div className="mt-4 rounded-lg bg-violet-50 dark:bg-violet-950/30 border border-violet-200 dark:border-violet-800 p-3 text-sm text-violet-800 dark:text-violet-200">
                🧠 Perfil de Afinidad Inteligente Activado. PsyTalent evaluará a los candidatos usando el benchmark experto para este puesto.
              </div>
            )}
          </div>

          <BatterySelectorList
            tests={availableTests}
            selected={selected}
            onToggle={toggle}
            jkGroupExpandedByDefault={selected.some((s) =>
              getInstrumentId(s).startsWith('JK_')
            )}
          />
          <Button onClick={save} disabled={saving}>
            {saving ? 'Guardando...' : 'Guardar batería'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
